/**
 * API 缓存工具
 * 实现 stale-while-revalidate 策略：
 * 1. 如果请求相同，立即返回缓存数据
 * 2. 异步更新数据
 * 3. 下次请求使用新数据
 *
 * 特性：
 * - LRU 淘汰策略，限制最大缓存数量
 * - 内存优化，避免无限增长
 * - Chrome 53+ 兼容
 * - 白名单机制，只对指定接口启用缓存
 */

import { API_PATHS } from '../services/apiPaths';

// 缓存项接口
interface CacheItem<T> {
  data: T;
  timestamp: number;
  accessTime: number; // 最后访问时间，用于 LRU
  promise?: Promise<T>; // 正在进行的请求 Promise
}

// 白名单匹配器类型
export type WhitelistMatcher = string | RegExp | ((url: string, method: string) => boolean);

// 缓存配置
export interface CacheConfig {
  /** 缓存过期时间（毫秒），默认 5 分钟 */
  maxAge?: number;
  /** 是否启用缓存，默认 true */
  enabled?: boolean;
  /** 最大缓存数量，默认 50，超出后使用 LRU 淘汰 */
  maxSize?: number;
  /** 白名单：只有匹配的接口才启用缓存。支持字符串、正则表达式或函数 */
  whitelist?: WhitelistMatcher[];
}

// 默认配置
const DEFAULT_CONFIG: Required<Omit<CacheConfig, 'whitelist'>> & { whitelist?: WhitelistMatcher[] } = {
  maxAge: 5 * 60 * 1000, // 5 分钟
  enabled: true,
  maxSize: 10, // 最多缓存 10 个请求
  // 默认白名单：只对首页、频道页、详情页启用缓存
  whitelist: [
    // 首页
    API_PATHS.GetHomePage,
    // 频道页
    // API_PATHS.RPCUnChannelPageData,
    // 详情页
    API_PATHS.getDetail,
    API_PATHS.getDetailPage,
  ],
};

// 缓存存储（使用 Map 保持插入顺序，Chrome 53+ 支持）
const cache = new Map<string, CacheItem<any>>();

/**
 * 安全的 JSON 序列化
 * 处理 undefined、函数、循环引用等边界情况
 */
function safeStringify(value: any): string {
  if (value === undefined || value === null) {
    return String(value);
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    // 使用 JSON.stringify 的 replacer 处理 undefined
    return JSON.stringify(value, function (key, val) {
      // 跳过 undefined 值
      if (val === undefined) {
        return null;
      }
      // 函数转为字符串标识
      if (typeof val === 'function') {
        return '[Function]';
      }
      return val;
    });
  } catch {
    // 处理循环引用等情况，使用类型标识
    return `[${Object.prototype.toString.call(value)}]`;
  }
}

/**
 * 生成缓存 key
 * @param method 请求方法 (GET/POST)
 * @param url 请求 URL
 * @param params 请求参数
 * @param body 请求体（POST）
 */
function generateCacheKey(method: string, url: string, params?: any, body?: any): string {
  const parts = [method.toUpperCase(), url];

  // 序列化参数（优化：只序列化非空对象）
  if (params && typeof params === 'object' && Object.keys(params).length > 0) {
    const sortedKeys = Object.keys(params).sort();
    const paramStr = sortedKeys
      .map((key) => {
        const val = params[key];
        // 跳过 undefined
        if (val === undefined) {
          return '';
        }
        return `${key}=${safeStringify(val)}`;
      })
      .filter(Boolean)
      .join('&');
    if (paramStr) {
      parts.push(paramStr);
    }
  }

  // 序列化请求体
  if (body !== undefined && body !== null) {
    parts.push(safeStringify(body));
  }

  return parts.join('|');
}

/**
 * 检查缓存是否有效
 */
function isCacheValid<T>(item: CacheItem<T>, maxAge: number): boolean {
  return Date.now() - item.timestamp < maxAge;
}

/**
 * 检查 URL 是否匹配白名单
 * @param url 请求 URL
 * @param method 请求方法
 * @param whitelist 白名单配置
 */
function isWhitelisted(url: string, method: string, whitelist?: WhitelistMatcher[]): boolean {
  // 如果没有白名单，默认允许（向后兼容）
  if (!whitelist || whitelist.length === 0) {
    return true;
  }

  // 检查是否匹配任一白名单规则
  for (const matcher of whitelist) {
    if (typeof matcher === 'string') {
      // 字符串精确匹配
      if (url === matcher || url.includes(matcher)) {
        return true;
      }
    } else if (matcher instanceof RegExp) {
      // 正则表达式匹配
      if (matcher.test(url)) {
        return true;
      }
    } else if (typeof matcher === 'function') {
      // 函数匹配
      if (matcher(url, method)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * 获取合并后的白名单
 * 如果请求配置中指定了 whitelist，使用配置的；否则使用默认白名单
 */
function getMergedWhitelist(config?: CacheConfig): WhitelistMatcher[] | undefined {
  // 如果配置中明确指定了 whitelist，使用配置的
  if (config?.whitelist !== undefined) {
    return config.whitelist;
  }
  // 否则使用默认白名单
  return DEFAULT_CONFIG.whitelist;
}

/**
 * LRU 淘汰：移除最久未访问的缓存项
 * 当缓存数量超过 maxSize 时调用
 */
function evictLRU(maxSize: number): void {
  // 持续删除直到满足大小限制
  while (cache.size > maxSize) {
    // 找到最久未访问的项
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    // Map 遍历在 Chrome 53+ 支持，但为了兼容性使用 Array.from
    const entries = Array.from(cache.entries());
    for (const [key, item] of entries) {
      // 优先删除没有 pending promise 的项
      if (!item.promise && item.accessTime < oldestTime) {
        oldestTime = item.accessTime;
        oldestKey = key;
      }
    }

    // 如果所有项都有 pending promise，删除最旧的（即使有 promise）
    if (oldestKey === null && entries.length > 0) {
      const [firstKey, firstItem] = entries[0];
      oldestKey = firstKey;
      oldestTime = firstItem.accessTime;
      for (let i = 1; i < entries.length; i++) {
        const [entryKey, entryItem] = entries[i];
        if (entryItem.accessTime < oldestTime) {
          oldestTime = entryItem.accessTime;
          oldestKey = entryKey;
        }
      }
    }

    // 删除最久未访问的项
    if (oldestKey !== null) {
      cache.delete(oldestKey);
    } else {
      // 如果仍然找不到（理论上不应该发生），强制删除第一个
      if (entries.length > 0) {
        cache.delete(entries[0][0]);
      }
      break; // 防止无限循环
    }
  }
}

/**
 * 获取缓存数据
 */
export function getCache<T>(
  method: string,
  url: string,
  params?: any,
  body?: any,
  config: CacheConfig = {}
): CacheItem<T> | null {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (!finalConfig.enabled) {
    return null;
  }

  // 检查白名单
  const whitelist = getMergedWhitelist(config);
  if (whitelist && !isWhitelisted(url, method, whitelist)) {
    return null;
  }

  const key = generateCacheKey(method, url, params, body);
  const item = cache.get(key);

  if (!item) {
    return null;
  }

  // 更新访问时间（LRU）
  item.accessTime = Date.now();

  // 检查缓存是否有效
  if (isCacheValid(item, finalConfig.maxAge)) {
    return item;
  }

  // 缓存已过期，但不删除，允许返回 stale 数据
  return item;
}

/**
 * 设置缓存数据
 */
export function setCache<T>(
  method: string,
  url: string,
  data: T,
  params?: any,
  body?: any,
  config: CacheConfig = {}
): void {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (!finalConfig.enabled) {
    return;
  }

  // 检查白名单
  const whitelist = getMergedWhitelist(config);
  if (whitelist && !isWhitelisted(url, method, whitelist)) {
    return;
  }

  const key = generateCacheKey(method, url, params, body);
  const now = Date.now();

  // 检查是否已存在（可能是 pending request）
  const existing = cache.get(key);
  if (existing) {
    // 更新现有项
    existing.data = data;
    existing.timestamp = now;
    existing.accessTime = now;
  } else {
    // 创建新项
    cache.set(key, {
      data,
      timestamp: now,
      accessTime: now,
    });

    // LRU 淘汰：如果超过最大数量，移除最久未访问的
    if (cache.size > finalConfig.maxSize) {
      evictLRU(finalConfig.maxSize);
    }
  }
}

/**
 * 设置正在进行的请求 Promise
 * 用于防止重复请求
 */
export function setPendingRequest<T>(
  method: string,
  url: string,
  promise: Promise<T>,
  params?: any,
  body?: any,
  config: CacheConfig = {}
): void {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // 检查白名单（pending request 也需要检查，确保一致性）
  const whitelist = getMergedWhitelist(config);
  if (whitelist && !isWhitelisted(url, method, whitelist)) {
    return;
  }

  const key = generateCacheKey(method, url, params, body);
  const item = cache.get(key);
  const now = Date.now();

  if (item) {
    item.promise = promise;
    item.accessTime = now; // 更新访问时间
  } else {
    // 如果缓存不存在，创建一个临时项来存储 promise
    cache.set(key, {
      data: null as any,
      timestamp: 0,
      accessTime: now,
      promise,
    });

    // LRU 淘汰：如果超过最大数量，移除最久未访问的
    if (cache.size > finalConfig.maxSize) {
      evictLRU(finalConfig.maxSize);
    }
  }
}

/**
 * 获取正在进行的请求 Promise
 */
export function getPendingRequest<T>(method: string, url: string, params?: any, body?: any): Promise<T> | null {
  const key = generateCacheKey(method, url, params, body);
  const item = cache.get(key);
  return item?.promise || null;
}

/**
 * 清除指定请求的 pending promise
 */
export function clearPendingRequest(method: string, url: string, params?: any, body?: any): void {
  const key = generateCacheKey(method, url, params, body);
  const item = cache.get(key);
  if (item) {
    delete item.promise;
  }
}

/**
 * 清除所有缓存
 */
export function clearAllCache(): void {
  cache.clear();
}

/**
 * 清除指定请求的缓存
 */
export function clearCache(method: string, url: string, params?: any, body?: any): void {
  const key = generateCacheKey(method, url, params, body);
  cache.delete(key);
}

/**
 * 获取缓存统计信息
 */
export function getCacheStats(): {
  size: number;
  maxSize: number;
  keys: string[];
  memoryEstimate?: number;
  defaultWhitelist?: WhitelistMatcher[];
} {
  // 估算内存占用（粗略计算）
  let memoryEstimate = 0;
  try {
    // Chrome 53+ 不支持 performance.memory，使用估算
    const avgItemSize = 1024; // 假设每个缓存项平均 1KB
    memoryEstimate = cache.size * avgItemSize;
  } catch {
    // 忽略错误
  }

  return {
    size: cache.size,
    maxSize: DEFAULT_CONFIG.maxSize,
    keys: Array.from(cache.keys()),
    memoryEstimate,
    defaultWhitelist: DEFAULT_CONFIG.whitelist ? [...DEFAULT_CONFIG.whitelist] : undefined,
  };
}
